import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus, 
  Sparkles, 
  Globe, 
  ArrowRight, 
  Trash2, 
  ExternalLink,
  Loader2,
  LayoutGrid,
  User,
  LogOut,
  Zap,
  Settings2,
  FolderOpen,
  Package,
  Code,
  Terminal,
  Bookmark,
  List,
  Target,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grouped dropdown for planned campaigns - shows only confirmed/bucketed items
function PlannedCampaignSelect({ value, groups, loading, onChange }) {
  const handleChange = (e) => {
    const playId = e.target.value;
    // Find the play across all groups
    let foundPlay = null;
    for (const group of groups) {
      const play = group.plays.find(p => p.play_id === playId);
      if (play) {
        foundPlay = play;
        break;
      }
    }
    onChange(playId, foundPlay?.landing_page_context || '');
  };
  
  // Build options array for rendering
  const options = [];
  for (const group of groups) {
    // Add optgroup start marker with bucket info
    options.push({ 
      type: 'group', 
      bucket_id: group.bucket_id, 
      plan_name: group.plan_name,
      bucket_status: group.bucket_status,
      bucket_date: group.bucket_date
    });
    // Add play options
    for (const play of group.plays) {
      options.push({ 
        type: 'option', 
        play_id: play.play_id, 
        label: `${play.play_name} (${play.stage} · ${(play.channel || '').replace('_', ' ')})` 
      });
    }
  }

  return (
    <select
      id="planned-campaign"
      value={value}
      onChange={handleChange}
      disabled={loading}
      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
      data-testid="planned-campaign-select"
    >
      <option value="">-- Select a confirmed campaign --</option>
      {renderGroupedOptions(options)}
    </select>
  );
}

// Helper function to render grouped options with bucket info
function renderGroupedOptions(options) {
  const result = [];
  let currentGroupItems = [];
  let currentGroup = null;
  
  for (let i = 0; i < options.length; i++) {
    const item = options[i];
    if (item.type === 'group') {
      // If we have a previous group, close it
      if (currentGroup !== null) {
        // Format: "Plan: PlanName — Status · Date"
        const label = `${currentGroup.plan_name} — ${currentGroup.bucket_status} · ${currentGroup.bucket_date}`;
        result.push(
          <optgroup key={currentGroup.bucket_id} label={label}>
            {currentGroupItems}
          </optgroup>
        );
        currentGroupItems = [];
      }
      currentGroup = item;
    } else {
      currentGroupItems.push(
        <option key={item.play_id} value={item.play_id}>
          {item.label}
        </option>
      );
    }
  }
  
  // Close the last group
  if (currentGroup !== null && currentGroupItems.length > 0) {
    const label = `${currentGroup.plan_name} — ${currentGroup.bucket_status} · ${currentGroup.bucket_date}`;
    result.push(
      <optgroup key={currentGroup.bucket_id} label={label}>
        {currentGroupItems}
      </optgroup>
    );
  }
  
  return result;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [apiCampaigns, setApiCampaigns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: "", url: "", mode: "manual", asset_id: "", planned_campaign_id: "", planned_campaign_context: "" });
  const [groupedData, setGroupedData] = useState(null);
  const [bucketListCount, setBucketListCount] = useState(0);
  const [plannedCampaigns, setPlannedCampaigns] = useState([]);
  const [loadingPlannedCampaigns, setLoadingPlannedCampaigns] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Use optimized dashboard summary endpoint for faster initial load
      const summaryRes = await axios.get(`${API}/dashboard-summary`);
      const summary = summaryRes.data;
      
      if (summary) {
        // Set campaigns from summary
        setCampaigns(summary.campaigns?.ui || []);
        setApiCampaigns(summary.campaigns?.api || []);
        setAssets((summary.assets || []).filter(a => a.status === 'ready'));
        setBucketListCount(summary.bucket_count || 0);
        
        // Set grouped data format for compatibility
        setGroupedData({
          ui_campaigns: {
            items: summary.campaigns?.ui || [],
            total: summary.campaigns?.total || 0,
            has_more: (summary.campaigns?.total || 0) > 5
          },
          api_campaigns: {
            items: summary.campaigns?.api || [],
            total: summary.campaigns?.api?.length || 0,
            has_more: false
          }
        });
      }
    } catch (error) {
      console.error("Dashboard summary failed, falling back to individual calls:", error);
      // Fallback to individual calls if summary endpoint fails
      try {
        const [campaignsRes, assetsRes] = await Promise.all([
          axios.get(`${API}/campaigns?limit=20`),
          axios.get(`${API}/assets`)
        ]);
        
        const allCampaigns = campaignsRes.data || [];
        const uiCampaigns = allCampaigns.filter(c => c.source !== 'api');
        const apiCampaignsList = allCampaigns.filter(c => c.source === 'api');
        
        setCampaigns(uiCampaigns);
        setApiCampaigns(apiCampaignsList);
        setAssets((assetsRes.data || []).filter(a => a.status === 'ready'));
      } catch (fallbackError) {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch planned campaigns when asset is selected
  const fetchPlannedCampaigns = async (assetId) => {
    if (!assetId) {
      setPlannedCampaigns([]);
      return;
    }
    
    setLoadingPlannedCampaigns(true);
    try {
      const response = await axios.get(`${API}/bucket-workflow/planned-campaigns?asset_id=${assetId}`);
      setPlannedCampaigns(response.data || []);
    } catch (error) {
      console.error('Failed to fetch planned campaigns:', error);
      setPlannedCampaigns([]);
    } finally {
      setLoadingPlannedCampaigns(false);
    }
  };

  // Group planned campaigns by bucket_id for dropdown rendering
  // Now groups by bucket (with plan name shown in header)
  const groupedPlannedCampaigns = useMemo(() => {
    if (!plannedCampaigns || plannedCampaigns.length === 0) {
      return [];
    }
    
    // Use a Map to group campaigns by bucket
    const bucketMap = new Map();
    
    for (const play of plannedCampaigns) {
      const bucketId = play.bucket_id;
      if (!bucketMap.has(bucketId)) {
        // Format date for display
        let dateStr = '';
        if (play.bucket_created_at) {
          const d = new Date(play.bucket_created_at);
          dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        
        bucketMap.set(bucketId, {
          bucket_id: bucketId,
          plan_name: play.plan_name,
          bucket_status: play.bucket_status || 'new',
          bucket_date: dateStr,
          plays: []
        });
      }
      bucketMap.get(bucketId).plays.push(play);
    }
    
    return Array.from(bucketMap.values());
  }, [plannedCampaigns]);

  const handleCreate = async () => {
    // If using an asset, URL is optional (will use asset's URL)
    if (!newCampaign.name.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    // If no asset selected, URL is required
    if (!newCampaign.asset_id && !newCampaign.url.trim()) {
      toast.error("Please enter a URL or select an asset");
      return;
    }

    // Basic URL validation (if URL provided)
    let url = newCampaign.url.trim();
    if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    setCreating(true);
    try {
      // Create campaign with optional asset_id and planned campaign context
      const payload = {
        name: newCampaign.name,
        mode: newCampaign.mode,
      };
      
      if (newCampaign.asset_id) {
        payload.asset_id = newCampaign.asset_id;
        // If using asset, we can still override URL or use asset's URL
        if (url) payload.url = url;
      } else {
        payload.url = url;
      }
      
      // If a planned campaign was selected, include the landing_page_context
      if (newCampaign.planned_campaign_id && newCampaign.planned_campaign_context) {
        payload.campaign_context = newCampaign.planned_campaign_context;
        payload.planned_campaign_play_id = newCampaign.planned_campaign_id;
      }
      
      const response = await axios.post(`${API}/campaigns`, payload);
      
      const campaignId = response.data.id;
      
      // If using an asset, campaign is pre-populated - go directly to workspace
      if (newCampaign.asset_id) {
        toast.success("Campaign created from asset!");
      } else {
        toast.success("Campaign created! Starting website scan...");
        // Start scraping only if not using an asset
        await axios.post(`${API}/campaigns/${campaignId}/scrape`);
        toast.success("Website scanned successfully!");
      }
      
      setCreateOpen(false);
      setNewCampaign({ name: "", url: "", mode: "manual", asset_id: "", planned_campaign_id: "", planned_campaign_context: "" });
      
      // Navigate to workspace
      navigate(`/campaign/${campaignId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, campaignId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      toast.success("Campaign deleted");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete campaign");
    }
  };

  // Handle URL params for creating from asset
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldCreate = params.get('create');
    const assetId = params.get('asset_id');
    
    if (shouldCreate === 'true' && assetId) {
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        setNewCampaign(prev => ({ 
          ...prev, 
          asset_id: assetId,
          url: asset.url,
          name: `${asset.name} Campaign`
        }));
        setCreateOpen(true);
        // Clean up URL
        window.history.replaceState({}, '', '/dashboard');
      }
    }
  }, [assets]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success("Logged out successfully");
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      scraping: { label: "Scanning...", class: "status-scraping" },
      scraped: { label: "Ready", class: "status-scraped" },
      analyzing: { label: "Analyzing...", class: "status-analyzing" },
      concerns_ready: { label: "Review Concerns", class: "status-scraped" },
      concerns_confirmed: { label: "Concerns Confirmed", class: "status-scraped" },
      generating_messaging: { label: "Generating...", class: "status-analyzing" },
      messaging_ready: { label: "Review Changes", class: "status-scraped" },
      messaging_confirmed: { label: "Changes Confirmed", class: "status-scraped" },
      active: { label: "Active", class: "status-active" },
    };
    const config = statusMap[status] || { label: status, class: "status-scraped" };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-semibold text-foreground font-['Outfit']">Pravik</span>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Assets Link */}
              <Button
                variant="ghost"
                onClick={() => navigate('/assets')}
                className="rounded-full"
                data-testid="assets-link"
              >
                <Package className="w-4 h-4 mr-2" />
                Assets
              </Button>
              
              {/* API Link */}
              <Button
                variant="ghost"
                onClick={() => navigate('/developers')}
                className="rounded-full"
                data-testid="api-link"
              >
                <Code className="w-4 h-4 mr-2" />
                API
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="rounded-full w-10 h-10 p-0"
                    data-testid="user-menu-btn"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-destructive cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Create Campaign Dialog (moved outside header) */}
      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) {
          // Reset form when dialog closes
          setNewCampaign({ name: "", url: "", mode: "manual", asset_id: "", planned_campaign_id: "", planned_campaign_context: "" });
          setPlannedCampaigns([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Create New Campaign</DialogTitle>
            <DialogDescription>
              {assets.length > 0 
                ? "Start from an asset or enter a new website URL." 
                : "Enter your campaign details. We'll scan the website to understand its content."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Asset Selection */}
            {assets.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="asset">Start from Asset (Optional)</Label>
                <select
                  id="asset"
                  value={newCampaign.asset_id}
                  onChange={(e) => {
                    const assetId = e.target.value;
                    const asset = assets.find(a => a.id === assetId);
                    setNewCampaign({ 
                      ...newCampaign, 
                      asset_id: assetId,
                      url: asset ? asset.url : newCampaign.url,
                      planned_campaign_id: '',
                      planned_campaign_context: ''
                    });
                    // Fetch planned campaigns for the selected asset
                    if (assetId) {
                      fetchPlannedCampaigns(assetId);
                    } else {
                      setPlannedCampaigns([]);
                    }
                  }}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="asset-select"
                >
                  <option value="">-- No asset (enter URL manually) --</option>
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.name} ({new URL(asset.url).hostname})
                    </option>
                  ))}
                </select>
                {newCampaign.asset_id && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    Campaign will inherit website context and policies from this asset
                  </p>
                )}
              </div>
            )}
            
            {/* Planned Campaign Selection - Only show when asset is selected */}
            {newCampaign.asset_id && (
              <div className="space-y-2">
                <Label htmlFor="planned-campaign">Planned Campaign (Optional)</Label>
                <PlannedCampaignSelect
                  value={newCampaign.planned_campaign_id}
                  groups={groupedPlannedCampaigns}
                  loading={loadingPlannedCampaigns}
                  onChange={(playId, context) => {
                    setNewCampaign({
                      ...newCampaign,
                      planned_campaign_id: playId,
                      planned_campaign_context: context
                    });
                  }}
                />
                {loadingPlannedCampaigns && (
                  <p className="text-xs text-muted-foreground">Loading confirmed campaigns...</p>
                )}
                {!loadingPlannedCampaigns && plannedCampaigns.length === 0 && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>No confirmed campaigns in Bucket List for this asset.</p>
                    <p className="text-purple-600">Go to a Plan → Step 3 → Confirm campaigns to add them.</p>
                  </div>
                )}
                {newCampaign.planned_campaign_id && newCampaign.planned_campaign_context && (
                  <p className="text-xs text-teal-600 flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    Campaign context will be pre-filled from this planned campaign
                  </p>
                )}
                {newCampaign.planned_campaign_id && !newCampaign.planned_campaign_context && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Selected planned campaign has no landing context
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                placeholder="e.g., Enterprise Visitors Campaign"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                data-testid="campaign-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">
                Website URL {newCampaign.asset_id ? '(Pre-filled from asset)' : ''}
              </Label>
            <Input
              id="url"
              placeholder="https://example.com"
              value={newCampaign.url}
              onChange={(e) => setNewCampaign({ ...newCampaign, url: e.target.value })}
              data-testid="campaign-url-input"
              disabled={!!newCampaign.asset_id}
              className={newCampaign.asset_id ? 'bg-muted' : ''}
            />
          </div>
          
          {/* Mode Toggle */}
          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-between py-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${newCampaign.mode === 'auto' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <Label htmlFor="mode-toggle" className="font-medium cursor-pointer">
                    Auto Mode
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  {newCampaign.mode === 'auto' 
                    ? "AI will automatically select best concerns & messaging" 
                    : "Manually review and approve each step"}
                </p>
              </div>
              <Switch
                id="mode-toggle"
                checked={newCampaign.mode === 'auto'}
                onCheckedChange={(checked) => setNewCampaign({ ...newCampaign, mode: checked ? 'auto' : 'manual' })}
                data-testid="mode-toggle"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => setCreateOpen(false)}
            className="rounded-full"
            data-testid="cancel-create-btn"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={creating}
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
            data-testid="submit-create-btn"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create & Scan
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        {/* Potential Campaigns Section - At the very top */}
        <div className="bg-purple-50/50 dark:bg-purple-900/10 px-6 py-6 rounded-2xl border border-purple-200/50 dark:border-purple-500/20 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground font-['Outfit'] flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-purple-500" />
                Potential Campaigns
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                AI-identified campaign opportunities from your asset analyses
              </p>
            </div>
            <Button 
              onClick={() => navigate('/potential-campaigns')}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-white rounded-full text-xs h-8"
              data-testid="go-to-potential-campaigns"
            >
              <Bookmark className="w-3.5 h-3.5 mr-1.5" />
              View Potential Campaigns
            </Button>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold text-foreground font-['Outfit'] mb-2">
                Your Campaigns
              </h1>
              <p className="text-muted-foreground">
                Manage your context-aware marketing campaigns
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Bucket List Button - always visible for workflow access */}
              <Button 
                onClick={() => navigate('/bucket-list')}
                variant="outline"
                size="sm"
                className="rounded-full text-sm h-9"
                data-testid="bucket-list-btn"
              >
                <List className="w-4 h-4 mr-2" />
                Bucket List {bucketListCount > 0 && `(${bucketListCount})`}
              </Button>
              
              {/* New Campaign Button */}
              <Button 
                onClick={() => setCreateOpen(true)}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                data-testid="create-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-surface rounded-2xl p-6 h-48 skeleton" />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-surface rounded-2xl border border-border p-12 text-center" data-testid="empty-state">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <LayoutGrid className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-foreground font-['Outfit'] mb-2">
              No campaigns yet
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create your first campaign to start personalizing your website for different visitor segments.
            </p>
            <Button 
              onClick={() => setCreateOpen(true)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
              data-testid="empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Campaign
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* UI Campaigns Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-foreground font-['Outfit'] flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Campaigns
                </h2>
                {campaigns.length > 4 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/campaigns')}
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    View all ({campaigns.length}) <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                )}
              </div>
              
              {campaigns.length === 0 ? (
                <div className="bg-surface rounded-xl border border-border p-6 text-center">
                  <p className="text-muted-foreground mb-4">No campaigns yet. Create your first campaign to get started.</p>
                  <Button 
                    onClick={() => setCreateOpen(true)}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Campaign
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {campaigns.slice(0, 4).map((campaign) => (
                    <div
                      key={campaign.id}
                      onClick={() => navigate(`/campaign/${campaign.id}`)}
                      className="bg-white border border-border rounded-xl p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all group"
                      data-testid={`campaign-card-${campaign.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Globe className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        </div>
                        <button
                          onClick={(e) => handleDelete(e, campaign.id)}
                          className="p-1.5 hover:bg-destructive/10 rounded-lg transition-all opacity-40 hover:opacity-100"
                          data-testid={`delete-campaign-${campaign.id}`}
                          type="button"
                          title="Delete campaign"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" strokeWidth={1.5} />
                        </button>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-foreground font-['Outfit'] mb-1.5 line-clamp-1">
                        {campaign.name}
                      </h3>
                      
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{campaign.url}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        {getStatusBadge(campaign.status)}
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
            </div>
            
            {/* API Campaigns Section */}
            <div className="bg-slate-50/50 dark:bg-slate-900/20 -mx-6 px-6 py-6 rounded-2xl border border-border/50">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground font-['Outfit'] flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-blue-500" />
                  API Campaigns
                  <span className="text-xs font-normal text-muted-foreground ml-1">Programmatic</span>
                </h2>
                <div className="flex items-center gap-2">
                  {apiCampaigns.length > 4 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/api-campaigns')}
                      className="text-muted-foreground hover:text-foreground text-xs"
                    >
                      View all ({apiCampaigns.length}) <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/developers')}
                    className="rounded-full text-xs h-8"
                  >
                    <Code className="w-3.5 h-3.5 mr-1.5" />
                    API Docs
                  </Button>
                </div>
              </div>
              
              {apiCampaigns.length === 0 ? (
                <div className="bg-white/50 dark:bg-slate-800/30 rounded-xl border border-border/50 p-6 text-center">
                  <Terminal className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Create campaigns programmatically via the API
                  </p>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/developers')}
                    className="rounded-full text-xs"
                  >
                    <Code className="w-3.5 h-3.5 mr-1.5" />
                    View Documentation
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {apiCampaigns.slice(0, 4).map((campaign) => (
                    <div
                      key={campaign.id}
                      onClick={() => navigate(`/campaign/${campaign.id}`)}
                      className="bg-white dark:bg-slate-800/50 border border-border/50 rounded-xl p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all group"
                      data-testid={`api-campaign-card-${campaign.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                          <Terminal className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                            API
                          </span>
                          <button
                            onClick={(e) => handleDelete(e, campaign.id)}
                            className="p-1.5 hover:bg-destructive/10 rounded-lg transition-all opacity-40 hover:opacity-100"
                            data-testid={`delete-api-campaign-${campaign.id}`}
                            type="button"
                            title="Delete campaign"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-destructive" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-foreground font-['Outfit'] mb-1.5 line-clamp-1">
                        {campaign.name}
                      </h3>
                      
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{campaign.url}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        {getStatusBadge(campaign.status)}
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Fast Campaigns Section */}
            <div className="bg-amber-50/50 dark:bg-amber-900/10 -mx-6 px-6 py-6 rounded-2xl border border-amber-200/50 dark:border-amber-500/20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground font-['Outfit'] flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Fast Campaigns
                  <span className="text-xs font-normal text-muted-foreground ml-1">Low latency personalization for conversations</span>
                </h2>
                <Button 
                  onClick={() => navigate('/fast-campaigns')}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs h-8"
                  data-testid="go-to-fast-campaigns"
                >
                  <Zap className="w-3.5 h-3.5 mr-1.5" />
                  Fast Campaigns
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
